import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { motion } from "framer-motion";
import { useAppearance } from "@/lib/appearance";
import { MotionStagger } from "@/lib/motion";
import { 
  ShieldCheck,
  Users, 
  TrendingUp, 
  BookOpen, 
  BarChart3,
  Award,
  Sparkles
} from "lucide-react";

interface Feature {
  icon: any;
  title: string;
  description: string;
  bullets?: string[];
  badge?: string;
  gradient: string;
  glowColor: string;
  testId?: string;
}

const features: Feature[] = [
  {
    icon: ShieldCheck,
    title: "Private by design",
    description: "Your recovery is yours. We never sell your information — ever.",
    bullets: [
      "Anonymous 8-character ForgeRestoreID — you choose what name (if any) to display",
      "Only people you connect with can see your shared content",
      "Granular privacy controls: you decide what to share, per category",
      "Crisis alerts go only to your chosen support network",
      "Export or delete your data anytime — and we never sell it to third parties",
    ],
    badge: "Privacy",
    gradient: "from-sky-500 to-indigo-600",
    glowColor: "rgba(56, 189, 248, 0.3)",
    testId: "feature-card-privacy",
  },
  {
    icon: Users,
    title: "Sponsor Matching",
    description: "Connect with experienced sponsors and build meaningful recovery relationships.",
    gradient: "from-emerald-500 to-green-600",
    glowColor: "rgba(16, 185, 129, 0.3)",
  },
  {
    icon: TrendingUp,
    title: "Progress Tracking",
    description: "Visualize your journey with color-coded milestone chips from 30 days to 5 years.",
    gradient: "from-pink-500 to-rose-600",
    glowColor: "rgba(236, 72, 153, 0.3)",
  },
  {
    icon: BookOpen,
    title: "Step Work Portal",
    description: "Organized step work across literature, writing, and discussion categories.",
    badge: "New",
    gradient: "from-amber-500 to-orange-600",
    glowColor: "rgba(245, 158, 11, 0.3)",
  },
  {
    icon: BarChart3,
    title: "Pattern Reflections",
    description: "Trend reflections from your own triggers, cravings, and journals — counts only, computed on your device.",
    gradient: "from-violet-500 to-purple-600",
    glowColor: "rgba(139, 92, 246, 0.3)",
  },
  {
    icon: Award,
    title: "Milestone Celebrations",
    description: "Track and celebrate recovery milestones with color-coded milestone chips.",
    gradient: "from-amber-500 to-yellow-500",
    glowColor: "rgba(245, 158, 11, 0.3)",
  },
];

export default function FeatureHighlights() {
  const { isReducedMotion } = useAppearance();
  return (
    <div className="space-y-8">
      <motion.div 
        initial={isReducedMotion ? false : { y: 20, opacity: 0 }}
        whileInView={isReducedMotion ? undefined : { y: 0, opacity: 1 }}
        animate={isReducedMotion ? { y: 0, opacity: 1 } : undefined}
        viewport={{ once: true }}
        transition={{ duration: 0.5 }}
        className="text-center"
      >
        <Badge 
          variant="outline" 
          className="mb-4 bg-background/80 backdrop-blur-sm border-primary/20 shadow-sm"
        >
          <Sparkles className="w-3 h-3 mr-1.5 text-amber-500" />
          Platform Features
        </Badge>
        <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold mb-3">
          With You in Recovery
        </h2>
        <p className="text-muted-foreground max-w-2xl mx-auto text-sm sm:text-base">
          An all-in-one platform for those in recovery to work the 12 steps
        </p>
      </motion.div>

      <MotionStagger
        className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5 sm:gap-6"
        delayBetween={0.08}
      >
        {features.map((feature, index) => {
          const Icon = feature.icon;
          return (
            <div key={index}>
              <Card 
                className="group relative overflow-hidden border bg-card shadow-lg hover:shadow-2xl transition-all duration-300 h-full" 
                data-testid={feature.testId ?? `feature-card-${index}`}
              >
                <div className="absolute inset-0 bg-gradient-to-br from-transparent via-transparent to-primary/5 opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                <CardHeader className="pb-3">
                  <div className="flex items-start justify-between gap-2 mb-3">
                    <div 
                      className={`relative p-3 rounded-xl bg-gradient-to-br ${feature.gradient} shadow-lg`}
                      style={{ boxShadow: `0 4px 14px ${feature.glowColor}` }}
                    >
                      <Icon className="w-5 h-5 text-white" />
                    </div>
                    {feature.badge && (
                      <Badge 
                        variant="secondary" 
                        className="text-xs font-semibold bg-primary/10 text-primary border-0"
                      >
                        {feature.badge}
                      </Badge>
                    )}
                  </div>
                  <CardTitle className="text-lg font-semibold">{feature.title}</CardTitle>
                </CardHeader>
                <CardContent className="pt-0">
                  <CardDescription className="text-sm leading-relaxed">
                    {feature.description}
                  </CardDescription>
                  {feature.bullets && (
                    <ul
                      className="mt-3 space-y-1.5 text-sm text-muted-foreground list-disc list-inside"
                      data-testid={`feature-card-bullets-${index}`}
                    >
                      {feature.bullets.map((b, i) => (
                        <li key={i}>{b}</li>
                      ))}
                    </ul>
                  )}
                </CardContent>
              </Card>
            </div>
          );
        })}
      </MotionStagger>
    </div>
  );
}
